import React, { useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useLanguage } from '../../../context/LanguageContext';
import {
  fetchLoggedInUserOrderAsync,
  selectUserInfoStatus,
  selectUserOrders,
} from '../userSlice';
import { Grid } from 'react-loader-spinner';
import { Link } from 'react-router-dom';
import {
  ClipboardDocumentListIcon,
  ShoppingBagIcon,
  UserIcon,
  EnvelopeIcon,
  PhoneIcon,
  KeyIcon,
  CheckCircleIcon,
  ClockIcon,
  TruckIcon,
} from '@heroicons/react/24/outline';

// Helper function to get multilingual text
function getMultilingualText(field, language = 'en') {
  if (!field) return '';
  if (typeof field === 'string') return field;
  if (typeof field === 'object') {
    const result = field[language] || field['en'] || field['fr'] || Object.values(field)[0] || '';
    return typeof result === 'string' ? result : '';
  }
  return '';
}

export default function UserOrders() {
  const dispatch = useDispatch();
  const { language } = useLanguage();
  const orders = useSelector(selectUserOrders);
  const status = useSelector(selectUserInfoStatus);

  useEffect(() => {
    dispatch(fetchLoggedInUserOrderAsync());
  }, [dispatch]);

  const getStatusColor = (orderStatus) => {
    switch (orderStatus) {
      case 'pending':
        return 'bg-amber-100 text-amber-700';
      case 'delivered':
        return 'bg-emerald-100 text-emerald-700';
      case 'received':
        return 'bg-sky-100 text-sky-700';
      default:
        return 'bg-slate-100 text-slate-700';
    }
  };

  const getStatusIcon = (orderStatus) => {
    switch (orderStatus) {
      case 'pending':
        return <ClockIcon className="w-4 h-4" />;
      case 'delivered':
        return <TruckIcon className="w-4 h-4" />;
      case 'received':
        return <CheckCircleIcon className="w-4 h-4" />;
      default:
        return <ClockIcon className="w-4 h-4" />;
    }
  };

  return (
    <div className="min-h-screen bg-slate-100 py-8 sm:py-12">
      <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8">
        {/* Page Header */}
        <div className="mb-8 flex items-center gap-3">
          <div className="inline-flex items-center justify-center w-12 h-12 rounded-xl bg-sky-500 text-white">
            <ClipboardDocumentListIcon className="w-6 h-6" />
          </div>
          <div>
            <h1 className="text-2xl sm:text-3xl font-light tracking-tight text-slate-900">
              My Orders
            </h1>
            <p className="text-slate-500 text-sm">
              View and track all your orders
            </p>
          </div>
        </div>

        {/* Loading State */}
        {status === 'loading' && (
          <div className="flex justify-center py-12">
            <Grid
              height="80"
              width="80"
              color="#0ea5e9"
              ariaLabel="grid-loading"
              radius="12.5"
              wrapperStyle={{}}
              wrapperClass=""
              visible={true}
            />
          </div>
        )}

        {/* Orders List */}
        {orders && orders.length > 0 ? (
          <div className="space-y-6">
            {orders.map((order) => (
              <div
                key={order.id}
                className="bg-white rounded-xl shadow-subtle border border-slate-200 overflow-hidden"
              >
                {/* Order Header */}
                <div className="p-6 border-b border-slate-200 bg-slate-50">
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                    <div>
                      <h2 className="text-lg font-medium text-slate-900">
                        Order #{order.id}
                      </h2>
                      <p className="text-sm text-slate-500 mt-1">
                        {order.totalItems} items
                      </p>
                    </div>
                    <span
                      className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium capitalize ${getStatusColor(
                        order.status
                      )}`}
                    >
                      {getStatusIcon(order.status)}
                      {order.status}
                    </span>
                  </div>
                </div>

                {/* Order Items */}
                <div className="divide-y divide-slate-200">
                  {order.items.map((item) => (
                    <div key={item.id} className="p-6">
                      <div className="flex gap-4">
                        <div className="h-20 w-20 flex-shrink-0 overflow-hidden rounded-xl border border-slate-200 bg-slate-100">
                          <img
                            src={item.product.thumbnail}
                            alt={getMultilingualText(item.product.title, language)}
                            className="h-full w-full object-cover object-center"
                          />
                        </div>
                        <div className="flex-1 min-w-0">
                          <h3 className="text-base font-medium text-slate-900">
                            <Link
                              to={`/product-detail/${item.product.id}`}
                              className="hover:text-sky-600 transition-colors"
                            >
                              {getMultilingualText(item.product.title, language)}
                            </Link>
                          </h3>
                          {item.product.brand && (
                            <p className="text-sm text-slate-500 mt-1">
                              {item.product.brand}
                            </p>
                          )}
                          <div className="mt-2 flex items-center gap-4">
                            <span className="text-sm text-slate-600">
                              Qty: {item.quantity}
                            </span>
                            <span className="text-sm font-medium text-slate-900">
                              €{item.product.discountPrice}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Order Summary */}
                <div className="p-6 bg-slate-50 border-t border-slate-200">
                  <div className="flex justify-between items-center mb-4">
                    <span className="text-base font-medium text-slate-900">
                      Total
                    </span>
                    <span className="text-lg font-medium text-slate-900">
                      €{order.totalAmount}
                    </span>
                  </div>

                  {/* Delivery Address */}
                  {order.selectedAddress && (
                  <div className="bg-white rounded-xl border border-slate-200 p-4">
                    <h4 className="text-sm font-medium text-slate-700 mb-3 flex items-center gap-2">
                      <ShoppingBagIcon className="w-4 h-4 text-sky-500" />
                      Delivery Details
                    </h4>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
                      <div className="flex items-start gap-2">
                        <UserIcon className="w-4 h-4 text-slate-400 mt-0.5" />
                        <div>
                          <p className="font-medium text-slate-900">
                            {order.selectedAddress.name}
                          </p>
                          <p className="text-slate-500">
                            {order.selectedAddress.contactNumber}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-start gap-2">
                        <EnvelopeIcon className="w-4 h-4 text-slate-400 mt-0.5" />
                        <div>
                          <p className="text-slate-500">
                            {order.selectedAddress.email}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-start gap-2">
                        <KeyIcon className="w-4 h-4 text-slate-400 mt-0.5" />
                        <div>
                          <p className="text-xs text-slate-400 uppercase tracking-wide">
                            Account
                          </p>
                          <p className="text-slate-700">
                            {order.selectedAddress.accountUsername}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        ) : (
          status !== 'loading' && (
            <div className="bg-white rounded-xl shadow-subtle border border-slate-200 p-12 text-center">
              <ClipboardDocumentListIcon className="w-16 h-16 text-slate-300 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-slate-900 mb-2">
                No Orders Yet
              </h3>
              <p className="text-slate-500 mb-6">
                Start shopping to see your orders here
              </p>
              <Link
                to="/"
                className="inline-flex items-center gap-2 bg-sky-500 hover:bg-sky-600 text-white font-medium py-3 px-6 rounded-xl transition-all duration-200 text-sm"
              >
                <ShoppingBagIcon className="w-4 h-4" />
                Browse Products
              </Link>
            </div>
          )
        )}
      </div>
    </div>
  );
}
